"use client"

import { useInViewport } from "ahooks"
import clsx from "clsx"
import { useEffect, useRef, useState } from "react"
import { npPageWidthClassName, npSectionMtClassName } from "src/components/np-design/components/constants"
import { useEventCallback } from "src/hooks/useEventCallback"
import { recordError } from "src/utilities/errorReporting"

export type NpVideoProps = {
  src: string
  title?: string
}

export const NpVideo = ({ src, title = "Video" }: NpVideoProps) => {
  const sectionRef = useRef<any>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [inViewOnce, setInViewOnce] = useState(false)
  const [inViewNow, setInViewNow] = useState(false)

  useInViewport(sectionRef, {
    callback: (entry) => {
      if (!inViewOnce && entry.isIntersecting) {
        setInViewOnce(true)
      }
      setInViewNow(entry.isIntersecting)
    },
  })

  const playVideo = useEventCallback(() => {
    videoRef.current?.play().catch((error) => recordError("Error playing video", error))
  })

  const pauseVideo = useEventCallback(() => {
    videoRef.current?.pause()
  })

  useEffect(() => {
    if (inViewNow) {
      playVideo()
    } else {
      pauseVideo()
    }
  }, [inViewNow, playVideo, pauseVideo])

  return (
    <section
      ref={sectionRef}
      title={title}
      aria-label={"-Video: " + title}
      className={clsx(npSectionMtClassName, npPageWidthClassName)}
    >
      <div
        aria-label={title}
        className={clsx(
          "w-page:rounded-3xl relative flex aspect-video items-center justify-center overflow-hidden bg-black",
          npPageWidthClassName,
        )}
      >
        {inViewOnce ? (
          <video ref={videoRef} controls autoPlay muted playsInline loop preload="none" className="size-full">
            <source src={src} type="video/mp4" />
            <track kind="captions" srcLang="de" src={src} />
          </video>
        ) : (
          <img src={src + "/ik-thumbnail.jpg"} alt={title} title={title} />
        )}
      </div>
    </section>
  )
}
